import {
  DEFAULT_VIEW_SORTALYS_SORT_BY_FILTERS,
  defaultViewAllSortByFiltersMap,
  defaultViewBackInStockSortByFiltersMap,
  defaultViewExpectedShortageSortByFiltersMap,
  defaultViewShortageSortByFiltersMap,
  defaultViewSortalysSortByFiltersMap,
} from 'src/app/defaults/filter.defaults';
import { ICountry } from 'src/app/entities/countries/state/country.model';
import {
  ProductViews,
  productViewToTabNameMap,
} from 'src/app/entities/products/enums/product-views.enum';
import {
  getActiveTableColumns,
  parseTableColumnConfig,
} from 'src/app/entities/products/functions/table-columns.functions';
import { getMapValueIfExists } from '@app-shared/functions/helpers.functions';
import { SessionRepository } from '@app-core/state/session.repository';
import { SortalysUserAccess } from '@app-shared/enums/sortalys-user-access.enum';
import { emitOnce } from '@ngneat/elf';

import { SortByFilter, SortByOrder } from '../enums/sort-by-filters.enum';
import { ShortageStatus } from '../enums/shortage-status.enum';
import { ProductsRepository } from '../state/products.repository';

export const ProductViewsActions = {
  VIEW_ALL: {
    setDefaultFilters: (
      productsRepo: ProductsRepository,
      activeCountry: ICountry,
    ) => {
      if (!productsRepo.getStoreProp('startDate'))
        productsRepo.setStartDate(null);

      emitOnce(() => {
        productsRepo.setStoreProp('shortageStatusFilter', null);
        productsRepo.setSortByFilter(
          getMapValueIfExists(
            defaultViewAllSortByFiltersMap,
            activeCountry.country_code,
            productsRepo.getStoreProp('sortBy')!,
          ),
        );
        productsRepo.setStoreProp(
          'activeSortByFilters',
          defaultViewAllSortByFiltersMap.get(activeCountry.country_code),
        );
        productsRepo.updateStoreProps({
          sortByOrder: SortByOrder.DESC,
          currentPage: 1,
        });
      });

      const tableColumns = parseTableColumnConfig(
        productsRepo.getStoreProp('tableColumnsConfig'),
        productViewToTabNameMap.get(ProductViews.ALL)!,
        activeCountry.country_code,
      );

      emitOnce(() => {
        productsRepo.setStoreProp('tableColumns', tableColumns);
        productsRepo.setStoreProp(
          'activeTableColumns',
          getActiveTableColumns(tableColumns),
        );

        productsRepo.selectionActions.setSelectedProducts([]);
        productsRepo.setCustomersFilter([]);
      });
    },
  },

  VIEW_SHORTAGE: {
    setDefaultFilters: (
      productsRepo: ProductsRepository,
      activeCountry: ICountry,
    ) => {
      if (!productsRepo.getStoreProp('startDate'))
        productsRepo.setStartDate(null);

      emitOnce(() => {
        productsRepo.setStoreProp('shortageStatusFilter', null);
        productsRepo.setSortByFilter(
          getMapValueIfExists(
            defaultViewShortageSortByFiltersMap,
            activeCountry.country_code,
            SortByFilter.NAME!, // default filter is Name https://teamwork.q.agency/app/tasks/16326387
          ),
        );
        productsRepo.setStoreProp(
          'activeSortByFilters',
          defaultViewShortageSortByFiltersMap.get(activeCountry.country_code),
        );
        // France needs to have sandoz last, see https://teamwork.q.agency/app/tasks/15068275
        productsRepo.updateStoreProps({
          sortByOrder: SortByOrder.ASC,
          currentPage: 1,
        });
      });

      const tableColumns = parseTableColumnConfig(
        productsRepo.getStoreProp('tableColumnsConfig'),
        productViewToTabNameMap.get(ProductViews.SHORTAGE)!,
        activeCountry.country_code,
      );

      emitOnce(() => {
        productsRepo.setStoreProp('tableColumns', tableColumns);
        productsRepo.setStoreProp(
          'activeTableColumns',
          getActiveTableColumns(tableColumns),
        );

        productsRepo.selectionActions.setSelectedProducts([]);
        productsRepo.setCustomersFilter([]);
      });
    },
  },

  EXPECTED_STOCK_OUT: {
    setDefaultFilters: (
      productsRepo: ProductsRepository,
      activeCountry: ICountry,
    ) => {
      if (!productsRepo.getStoreProp('startDate'))
        productsRepo.setStartDate(null);

      emitOnce(() => {
        productsRepo.setStoreProp('shortageStatusFilter', null);
        productsRepo.setSortByFilter(
          getMapValueIfExists(
            defaultViewShortageSortByFiltersMap,
            activeCountry.country_code,
            productsRepo.getStoreProp('sortBy')!,
          ),
        );
        productsRepo.setStoreProp(
          'activeSortByFilters',
          defaultViewExpectedShortageSortByFiltersMap.get(
            activeCountry.country_code,
          ),
        );
        productsRepo.updateStoreProps({
          sortByOrder: SortByOrder.DESC,
          currentPage: 1,
        });
      });

      const tableColumns = parseTableColumnConfig(
        productsRepo.getStoreProp('tableColumnsConfig'),
        productViewToTabNameMap.get(ProductViews.EXPECTED_STOCK_OUT)!,
        activeCountry.country_code,
      );

      emitOnce(() => {
        productsRepo.setStoreProp('tableColumns', tableColumns);
        productsRepo.setStoreProp(
          'activeTableColumns',
          getActiveTableColumns(tableColumns),
        );

        productsRepo.selectionActions.setSelectedProducts([]);
        productsRepo.setCustomersFilter([]);
      });
    },
  },

  VIEW_BACK_IN_STOCK: {
    setDefaultFilters: (
      productsRepo: ProductsRepository,
      activeCountry: ICountry,
    ) => {
      if (!productsRepo.getStoreProp('startDate'))
        productsRepo.setStartDate(null);

      emitOnce(() => {
        productsRepo.setStoreProp('shortageStatusFilter', null);
        productsRepo.setSortByFilter(
          getMapValueIfExists(
            defaultViewBackInStockSortByFiltersMap,
            activeCountry.country_code,
            productsRepo.getStoreProp('sortBy')!,
          ),
        );
        productsRepo.setStoreProp(
          'activeSortByFilters',
          defaultViewBackInStockSortByFiltersMap.get(
            activeCountry.country_code,
          ),
        );
        productsRepo.updateStoreProps({
          sortByOrder: SortByOrder.DESC,
          currentPage: 1,
        });
      });

      const tableColumns = parseTableColumnConfig(
        productsRepo.getStoreProp('tableColumnsConfig'),
        productViewToTabNameMap.get(ProductViews.BACK_IN_STOCK)!,
        activeCountry.country_code,
      );

      emitOnce(() => {
        productsRepo.setStoreProp('tableColumns', tableColumns);
        productsRepo.setStoreProp(
          'activeTableColumns',
          getActiveTableColumns(tableColumns),
        );

        productsRepo.selectionActions.setSelectedProducts([]);
        productsRepo.setCustomersFilter([]);
      });
    },
  },

  VIEW_NOVELTIES: {
    setDefaultFilters: (
      productsRepo: ProductsRepository,
      activeCountry: ICountry,
      sessionRepo: SessionRepository,
    ) => {
      if (!productsRepo.getStoreProp('startDate'))
        productsRepo.setStartDate(null);

      emitOnce(() => {
        productsRepo.setStoreProp('shortageStatusFilter', null);
        productsRepo.setSortByFilter(
          getMapValueIfExists(
            defaultViewAllSortByFiltersMap,
            activeCountry.country_code,
            productsRepo.getStoreProp('sortBy')!,
          ),
        );
        productsRepo.setStoreProp(
          'activeSortByFilters',
          defaultViewAllSortByFiltersMap.get(activeCountry.country_code),
        );
        productsRepo.updateStoreProps({
          sortByOrder: SortByOrder.DESC,
          currentPage: 1,
        });
      });

      const tableColumns = parseTableColumnConfig(
        productsRepo.getStoreProp('tableColumnsConfig'),
        productViewToTabNameMap.get(ProductViews.NOVELTIES)!,
        activeCountry.country_code,
        sessionRepo.getStoreProp('authUser'), // needed for checking if user is VIP
      );

      emitOnce(() => {
        productsRepo.setStoreProp('tableColumns', tableColumns);
        productsRepo.setStoreProp(
          'activeTableColumns',
          getActiveTableColumns(tableColumns),
        );

        productsRepo.selectionActions.setSelectedProducts([]);
        productsRepo.setCustomersFilter([]);
      });
    },
  },

  VIEW_SORTALYS: {
    setDefaultFilters: (
      productsRepo: ProductsRepository,
      activeCountry: ICountry,
      sessionRepo: SessionRepository,
    ) => {
      if (!productsRepo.getStoreProp('startDate'))
        productsRepo.setStartDate(null);

      emitOnce(() => {
        productsRepo.setStoreProp('shortageStatusFilter', [
          ShortageStatus.BackInStock,
        ]);

        productsRepo.setSortByFilter(
          getMapValueIfExists(
            defaultViewSortalysSortByFiltersMap,
            activeCountry.country_code,
            sessionRepo.getStoreProp('authUser')?.sortalys.sortalys_access ===
              SortalysUserAccess.SALES_REPRESENTATIVE
              ? SortByFilter.NEW_LISTINGS
              : productsRepo.getStoreProp('sortBy')!,
          ),
        );
        productsRepo.setStoreProp(
          'activeSortByFilters',
          DEFAULT_VIEW_SORTALYS_SORT_BY_FILTERS,
        );
        productsRepo.updateStoreProps({
          sortByOrder: SortByOrder.DESC,
          currentPage: 1,
        });
      });

      const tableColumns = parseTableColumnConfig(
        productsRepo.getStoreProp('tableColumnsConfig'),
        productViewToTabNameMap.get(ProductViews.SORTALYS)!,
        activeCountry.country_code,
      );

      emitOnce(() => {
        productsRepo.setStoreProp('tableColumns', tableColumns);
        productsRepo.setStoreProp(
          'activeTableColumns',
          getActiveTableColumns(tableColumns),
        );

        productsRepo.selectionActions.setSelectedProducts([]);
        productsRepo.setCustomersFilter([]);
      });
    },
  },

  VIEW_ORDERS: {
    setDefaultFilters: (
      productsRepo: ProductsRepository,
      activeCountry: ICountry,
    ) => {
      if (!productsRepo.getStoreProp('startDate'))
        productsRepo.setStartDate(null);

      emitOnce(() => {
        productsRepo.setStoreProp('shortageStatusFilter', null);
        productsRepo.updateStoreProps({ sortBy: null, currentPage: 1 });
        productsRepo.updateStoreProps({ sortByOrder: null, currentPage: 1 });
        productsRepo.setStoreProp('activeSortByFilters', null);
      });

      const tableColumns = parseTableColumnConfig(
        productsRepo.getStoreProp('tableColumnsConfig'),
        productViewToTabNameMap.get(ProductViews.ORDERS)!,
        activeCountry.country_code,
      );

      emitOnce(() => {
        productsRepo.setStoreProp('tableColumns', tableColumns);
        productsRepo.setStoreProp(
          'activeTableColumns',
          getActiveTableColumns(tableColumns),
        );

        productsRepo.selectionActions.setSelectedProducts([]);
        productsRepo.setCustomersFilter([]);
      });
    },
  },
};
